import React, {useEffect, useState} from "react";

// Models
import {AuthUser} from "../../../Models/DTOs/AuthUser";

// Components
import InputCheckbox from "../../Inputs/InputCheckbox";
import ButtonPill from "../../Buttons/ButtonPill";

// Hooks
import {useUpdateMarketingPreferences} from "../../../Hooks/User/useUpdateMarketingPreferences";
import ErrorMessage from "../../Text/ErrorMessage";
import SuccessMessage from "../../Text/SuccessMessage";
import LoadingOverlay from "../../LoadingOverlay";

type AccountMarketingPreferencesProps = {
    userData: AuthUser | null;
    isUserDataLoading: boolean;
    getValidAccessToken: () => Promise<string | null>;
};

const AccountMarketingPreferences: React.FC<AccountMarketingPreferencesProps> = (
    {
        userData,
        isUserDataLoading,
        getValidAccessToken,
    }
) => {
    const
        [allowCommercialEmails, setAllowCommercialEmails] = useState<boolean>(false),
        [allowDailyNewsletter, setAllowDailyNewsletter] = useState<boolean>(false),

        {
            updateMarketingPrefs,
            isUpdating,
            errorMessage,
            success,
            clearError,
            clearSuccess,
        } = useUpdateMarketingPreferences({getValidAccessToken}),

        handleChangeCommercial = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (errorMessage) clearError();
            if (success) clearSuccess();
            setAllowCommercialEmails(e.target.checked);
        },

        handleChangeDaily = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (errorMessage) clearError();
            if (success) clearSuccess();
            setAllowDailyNewsletter(e.target.checked);
        },

        handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            clearSuccess();
            await updateMarketingPrefs({
                allowCommercialEmails,
                allowDailyNewsletter,
            });
        };

    // Initialize from userData when it loads
    useEffect(() => {
        if (userData) {
            // fall back to false if null/undefined
            setAllowCommercialEmails(!!userData.allow_commercial_emails);
            setAllowDailyNewsletter(!!userData.allow_daily_newsletter);
        }
    }, [userData]);


    return (
        <form onSubmit={handleSubmit} className="account__marketing-prefs">
            <div className="account__row">
                {isUserDataLoading ? (
                    <LoadingOverlay size="full-cover" />
                ) : (
                    <div className="row__inner">
                        <InputCheckbox
                            name="allowCommercialEmails"
                            checked={allowCommercialEmails}
                            onChange={handleChangeCommercial}
                            label="I want to receive offers, updates, and marketing emails."
                        />
                    </div>
                )}
            </div>

            <div className="account__row">
                {isUserDataLoading ? (
                    <LoadingOverlay size="full-cover" />
                ) : (
                    <div className="row__inner">
                        <InputCheckbox
                            name="allowDailyNewsletter"
                            checked={allowDailyNewsletter}
                            onChange={handleChangeDaily}
                            label="I want to receive the daily newsletter."
                        />
                    </div>
                )}
            </div>

            <div className="account__row">
                <div className="row__inner">
                    <ButtonPill
                        type="submit"
                        label={isUpdating ? "Saving..." : "Save preferences"}
                        disabled={isUpdating || isUserDataLoading}
                    />
                </div>
            </div>

            {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
            {success && <SuccessMessage successMessage="Preferences updated" />}
        </form>
    );
};

export default AccountMarketingPreferences;
